import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getWatchBrands,
  getBrandImportSources,
  getAllBrandImportSources,
  brandHasShopifySource,
  saveBrandImportSources,
  createEmptyImportSource,
  updateWatchBrand,
  appendImportRun,
  fetchHtmlServerSide,
  fetchStoreApiText,
  type ExtractedProduct,
} from '../../services/firebase';
import {
  loadSquareSource,
  type SquareEntry,
} from '../CuratedWatches/SquareOnlineScraper';
import { canPasteInstead, loadWooSource, type WooEntry } from '../CuratedWatches/WooCommerceScraper';
import { wooPasteUrl } from '../CuratedWatches/storeJsonPaste';
import type { ScrapedWatchEntry } from '../CuratedWatches/BulkImport';
import { PasteStoreJsonModal } from './PasteStoreJsonModal';
import { storeScopeSummary, storeVariantSummary } from '../CuratedWatches/storeScope';
import { prefillShopUrl } from './prefillSourceUrl';
import type {
  WatchBrand,
  BrandImportSources,
  ImportSourceConfig,
} from '../../types';
import { Button } from '../common/Button';
import { BulkImport, type BulkImportStats, type StoreCategoryPickerConfig } from '../CuratedWatches/BulkImport';
import { SourceCard } from './SourceCard';
import { ImportHistoryPanel } from './ImportHistoryPanel';
import { describeError } from './errorUtils';

interface ImportSourcesPageProps {
  initialBrandId?: string;
  onBrandConsumed?: () => void;
  // The brand the user came from (origin of a Bulk Import / Manage Import
  // Sources navigation). Drives the "Back to brand refs" affordance below;
  // both undefined when the user opened this page directly from the top nav.
  backToBrandId?: string;
  backToBrandName?: string;
  onBackToBrandRefs?: () => void;
}

export function ImportSourcesPage({
  initialBrandId,
  onBrandConsumed,
  backToBrandId,
  backToBrandName,
  onBackToBrandRefs,
}: ImportSourcesPageProps) {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(
    initialBrandId ?? null,
  );
  const [search, setSearch] = useState('');
  // "Shopify only" sidebar filter. Off by default (shows every brand). The
  // per-brand source list isn't loaded up front, so when first enabled we fetch
  // every brand's import-sources doc once and cache it for the session.
  const [shopifyOnly, setShopifyOnly] = useState(false);
  const [sourcesByBrand, setSourcesByBrand] = useState<Map<
    string,
    ImportSourceConfig[]
  > | null>(null);
  const [loadingShopifyFilter, setLoadingShopifyFilter] = useState(false);
  const [config, setConfig] = useState<BrandImportSources | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [importingSourceId, setImportingSourceId] = useState<string | null>(null);
  // Products the launching source card already extracted in its Test preview
  // (auto-extracted or hand-pasted). Handed to the wizard so a site that blocks
  // us isn't re-fetched from scratch the moment the user clicks Import Now.
  const [importPrefetched, setImportPrefetched] = useState<
    Array<{ url: string; extracted: ExtractedProduct }>
  >([]);
  // Square Online / WooCommerce: the category's products, already mapped to
  // Bulk Import entries, loaded on Import Now and handed to the wizard as its
  // input.
  const [importStoreEntries, setImportStoreEntries] = useState<
    Array<SquareEntry | WooEntry | ScrapedWatchEntry> | null
  >(null);
  // Shop-URL store runs (not category URLs): the category picker's labels and
  // the source's remembered exclusions. Null = no picker (category run, paste).
  const [importStoreCategories, setImportStoreCategories] = useState<Omit<
    StoreCategoryPickerConfig,
    'onExclusionsSaved'
  > | null>(null);
  // The manual "paste the JSON" fallback for a Square Online / WooCommerce
  // source whose shop refused our requests (or opened by hand from the card).
  const [pasteFor, setPasteFor] = useState<{
    source: Extract<ImportSourceConfig, { kind: 'woocommerce' | 'square-online' }>;
    reason: string | null;
  } | null>(null);

  /**
   * A store load failed. If pasting can get round it (refused / unreachable),
   * say exactly which address to open and open the paste dialog; otherwise
   * just report it.
   */
  const handleStoreLoadFailure = (
    source: Extract<ImportSourceConfig, { kind: 'woocommerce' | 'square-online' }>,
    err: unknown,
    toastId: string,
  ) => {
    const message = err instanceof Error ? err.message : 'Could not read the shop';
    if (!canPasteInstead(err)) {
      toast.error(message, { id: toastId });
      return;
    }
    const openUrl =
      source.kind === 'woocommerce' ? wooPasteUrl(source.categoryUrl) : source.categoryUrl.trim();
    toast.error(
      `${message} Open ${openUrl} in your browser, copy the ${
        source.kind === 'woocommerce' ? 'response' : 'page source'
      }, and paste it in the dialog.`,
      { id: toastId, duration: 10000 },
    );
    setPasteFor({ source, reason: message });
  };
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  // When checked, extractions bypass the Firestore extraction_cache doc (but
  // still write fresh results back). Applies to both the "Test" preview and
  // the "Import Now" auto-import. Useful after deploying an extractor fix.
  const [skipCache, setSkipCache] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBrands(true);
      try {
        const all = await getWatchBrands();
        if (cancelled) return;
        all.sort((a, b) => a.name.localeCompare(b.name));
        setBrands(all);
      } catch (err) {
        console.error('Failed to load brands:', err);
        if (!cancelled) toast.error('Failed to load brands');
      } finally {
        if (!cancelled) setLoadingBrands(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (initialBrandId && initialBrandId !== selectedBrandId) {
      setSelectedBrandId(initialBrandId);
      onBrandConsumed?.();
    }
  }, [initialBrandId, selectedBrandId, onBrandConsumed]);

  useEffect(() => {
    if (!selectedBrandId) {
      setConfig(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingConfig(true);
      setConfigError(null);
      try {
        const next = await getBrandImportSources(selectedBrandId);
        if (!cancelled) setConfig(next);
      } catch (err) {
        console.error('Failed to load import sources:', err);
        if (!cancelled) {
          const msg = describeError(err);
          setConfigError(msg);
          toast.error(`Failed to load import sources — ${msg}`);
        }
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBrandId]);

  // Lazily load every brand's import sources the first time the "Shopify only"
  // filter is switched on, then keep the result cached for the session.
  useEffect(() => {
    if (!shopifyOnly || sourcesByBrand !== null) return;
    let cancelled = false;
    (async () => {
      setLoadingShopifyFilter(true);
      try {
        const map = await getAllBrandImportSources();
        if (!cancelled) setSourcesByBrand(map);
      } catch (err) {
        console.error('Failed to load brand import sources for filter:', err);
        if (!cancelled) {
          toast.error('Failed to apply Shopify filter');
          setShopifyOnly(false);
        }
      } finally {
        if (!cancelled) setLoadingShopifyFilter(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shopifyOnly, sourcesByBrand]);

  const filteredBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = brands;
    if (q) {
      result = result.filter(
        (b) =>
          b.name.toLowerCase().includes(q) ||
          (b.displayName ?? '').toLowerCase().includes(q),
      );
    }
    if (shopifyOnly && sourcesByBrand) {
      result = result.filter((b) => brandHasShopifySource(b, sourcesByBrand));
    }
    return result;
  }, [brands, search, shopifyOnly, sourcesByBrand]);

  const selectedBrand = useMemo(
    () => brands.find((b) => b.id === selectedBrandId) ?? null,
    [brands, selectedBrandId],
  );

  // Label for the "Back to brand refs" link. Prefer the live brand record once
  // brands have loaded; fall back to the name handed down by the parent, then a
  // generic label so the link is never blank mid-load.
  const backBrandLabel = useMemo(() => {
    const fromList = backToBrandId
      ? brands.find((b) => b.id === backToBrandId)
      : undefined;
    return fromList?.displayName || fromList?.name || backToBrandName || 'brand';
  }, [brands, backToBrandId, backToBrandName]);

  const hasShopifySource = useMemo(
    () => (config?.sources ?? []).some((s) => s.kind === 'shopify'),
    [config],
  );

  const handleAddSource = (kind: ImportSourceConfig['kind']) => {
    if (!config) return;
    if (kind === 'shopify' && hasShopifySource) return;
    let source = createEmptyImportSource(kind);
    // Prefill a new Shopify source's store URL with the brand's website, mirroring
    // the "Use <domain>" affordance in the Bulk Import wizard. Sensible default
    // only — the user can edit it before saving; blank if the brand has no URL.
    if (source.kind === 'shopify' && selectedBrand?.mainWebsite) {
      source = { ...source, storeUrl: selectedBrand.mainWebsite.replace(/\/+$/, '') };
    }
    // Same for Square Online / WooCommerce: the brand's home page is the
    // "import the whole shop" URL — the usual single-brand case. Blank if the
    // brand has no website.
    if (source.kind === 'square-online' || source.kind === 'woocommerce') {
      source = { ...source, categoryUrl: prefillShopUrl(selectedBrand?.mainWebsite) };
    }
    const next: BrandImportSources = {
      ...config,
      sources: [...config.sources, source],
    };
    setConfig(next);
  };

  const handleUpdateSource = (id: string, updated: ImportSourceConfig) => {
    if (!config) return;
    setConfig({
      ...config,
      sources: config.sources.map((s) => (s.id === id ? updated : s)),
    });
  };

  const handleRemoveSource = (id: string) => {
    if (!config) return;
    if (!window.confirm('Remove this import source?')) return;
    setConfig({
      ...config,
      sources: config.sources.filter((s) => s.id !== id),
    });
  };

  const handleImportNow = async (
    source: ImportSourceConfig,
    prefetched: Array<{ url: string; extracted: ExtractedProduct }> = [],
  ) => {
    if (!selectedBrandId || !config) return;
    if (source.kind === 'square-online') {
      // No legacy brand-doc bridge to sync: the wizard gets the products
      // directly. One category imports under this brand (v1).
      const loadingToast = toast.loading('Reading the Square Online shop…');
      try {
        await saveBrandImportSources(selectedBrandId, config.sources);
        const loaded = await loadSquareSource(source.categoryUrl, {
          fetchPageHtml: fetchHtmlServerSide,
          onPage: (page, total) =>
            toast.loading(`Fetching products… page ${page} of ${total}`, { id: loadingToast }),
          expandVariants: source.expandVariants !== false,
        });
        if (loaded.entries.length === 0) {
          toast.error(`No visible products: ${storeScopeSummary(0, loaded.scope, loaded.category?.name)}.`, {
            id: loadingToast,
          });
          return;
        }
        toast.success(
          storeScopeSummary(loaded.products.length, loaded.scope, loaded.category?.name) +
            storeVariantSummary(loaded),
          { id: loadingToast, duration: 6000 },
        );
        setImportPrefetched([]);
        setImportStoreEntries(loaded.entries);
        // A whole-shop run opens on the category picker; a category URL has
        // already filtered. Square's storefront list is flat (no parents).
        setImportStoreCategories(
          loaded.scope === 'shop' ? { savedExcluded: source.excludedCategoryIds } : null,
        );
        setImportingSourceId(source.id);
      } catch (err) {
        console.error('Square Online load failed:', err);
        handleStoreLoadFailure(source, err, loadingToast);
      }
      return;
    }
    if (source.kind === 'woocommerce') {
      const loadingToast = toast.loading('Reading the WooCommerce shop…');
      try {
        await saveBrandImportSources(selectedBrandId, config.sources);
        const loaded = await loadWooSource(source.categoryUrl, {
          fetchText: fetchStoreApiText,
          onPage: (page) => toast.loading(`Fetching products… page ${page}`, { id: loadingToast }),
          expandVariants: source.expandVariants !== false,
        });
        if (loaded.entries.length === 0) {
          toast.error(`No products: ${storeScopeSummary(0, loaded.scope, loaded.category?.name)}.`, {
            id: loadingToast,
          });
          return;
        }
        toast.success(
          storeScopeSummary(loaded.products.length, loaded.scope, loaded.category?.name) +
            storeVariantSummary(loaded),
          { id: loadingToast, duration: 6000 },
        );
        setImportPrefetched([]);
        setImportStoreEntries(loaded.entries);
        setImportStoreCategories(
          loaded.scope === 'shop'
            ? {
                savedExcluded: source.excludedCategoryIds,
                hierarchy: {
                  names: new Map(loaded.categories.map((c) => [String(c.id), c.name])),
                  parents: new Map(loaded.categories.map((c) => [String(c.id), String(c.parent ?? 0)])),
                },
              }
            : null,
        );
        setImportingSourceId(source.id);
      } catch (err) {
        console.error('WooCommerce load failed:', err);
        handleStoreLoadFailure(source, err, loadingToast);
      }
      return;
    }
    try {
      await saveBrandImportSources(selectedBrandId, config.sources);
      // Sync this source's fields back to the brand doc so BulkImport (which
      // still reads legacy fields) sees the up-to-date config. This bridge
      // goes away once BulkImport reads from BrandImportSources directly.
      if (source.kind === 'shopify') {
        await updateWatchBrand(selectedBrandId, {
          shopifyEnabled: true,
          shopifyUrl: source.storeUrl,
          excludedProductTypes: source.excludedProductTypes,
          importSource: 'shopify',
        });
      } else if (source.kind === 'generic') {
        await updateWatchBrand(selectedBrandId, {
          shopifyEnabled: false,
          productUrls: source.productUrls,
          importSource: 'generic',
        });
      }
      setBrands((prev) =>
        prev.map((b) => {
          if (b.id !== selectedBrandId) return b;
          if (source.kind === 'shopify') {
            return {
              ...b,
              shopifyEnabled: true,
              shopifyUrl: source.storeUrl,
              excludedProductTypes: source.excludedProductTypes,
              importSource: 'shopify',
            };
          }
          if (source.kind === 'generic') {
            return {
              ...b,
              shopifyEnabled: false,
              productUrls: source.productUrls,
              importSource: 'generic',
            };
          }
          return b;
        }),
      );
      setImportStoreEntries(null);
      setImportStoreCategories(null);
      setImportPrefetched(prefetched);
      setImportingSourceId(source.id);
    } catch (err) {
      console.error('Failed to prep import:', err);
      toast.error('Could not prepare import');
    }
  };

  const importingSource = useMemo(
    () =>
      importingSourceId
        ? config?.sources.find((s) => s.id === importingSourceId) ?? null
        : null,
    [config, importingSourceId],
  );

  const autoStartUrlsForImport = useMemo(() => {
    if (!importingSource || importingSource.kind !== 'generic') return undefined;
    const directUrls = importingSource.productUrls ?? [];
    const collectionPageUrls = importingSource.collectionPageUrls ?? [];
    const jsonListingUrl = importingSource.jsonListingUrl?.trim();
    if (directUrls.length === 0 && collectionPageUrls.length === 0 && !jsonListingUrl) return undefined;
    // Both overrides travel: productLink drives the listing crawl, image the
    // per-product extraction. (Only productLink used to be forwarded, which is
    // why a configured image selector never reached the extractor at all.)
    const productLink = importingSource.cssSelectorOverrides?.productLink?.trim();
    const image = importingSource.cssSelectorOverrides?.image?.trim();
    const cssSelectorOverrides =
      productLink || image
        ? { ...(productLink ? { productLink } : {}), ...(image ? { image } : {}) }
        : undefined;
    return {
      directUrls,
      collectionPageUrls,
      cssSelectorOverrides,
      excludedUrls: importingSource.excludedUrls,
      extractionMethod: importingSource.extractionMethod,
      jsonListingUrl: jsonListingUrl || undefined,
    };
  }, [importingSource]);

  const handleSave = async () => {
    if (!config || !selectedBrandId) return;
    setSaving(true);
    try {
      await saveBrandImportSources(selectedBrandId, config.sources);
      // When the last usable Shopify source is gone (removed, switched to
      // another kind, or its URL cleared), clear the legacy shopifyEnabled /
      // shopifyUrl mirror on the brand doc so it stops drifting. We only ever
      // clear here — adding/changing sources never touches the legacy fields.
      const stillHasShopify = config.sources.some(
        (s) => s.kind === 'shopify' && Boolean(s.storeUrl?.trim()),
      );
      const brandHasLegacyShopify = Boolean(
        selectedBrand?.shopifyEnabled || selectedBrand?.shopifyUrl,
      );
      if (!stillHasShopify && brandHasLegacyShopify) {
        await updateWatchBrand(selectedBrandId, {
          shopifyEnabled: false,
          shopifyUrl: '',
        });
        setBrands((prev) =>
          prev.map((b) =>
            b.id === selectedBrandId
              ? { ...b, shopifyEnabled: false, shopifyUrl: '' }
              : b,
          ),
        );
      }
      toast.success('Import sources saved');
    } catch (err) {
      console.error('Failed to save import sources:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {onBackToBrandRefs && (
        <button
          onClick={onBackToBrandRefs}
          className="text-sm text-brand-600 hover:underline"
        >
          ← Back to {backBrandLabel} references
        </button>
      )}
      <div className="grid grid-cols-12 gap-6">
      <aside className="col-span-12 md:col-span-4 lg:col-span-3">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Brands</h2>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search brands…"
            className="w-full text-sm border border-gray-300 rounded px-2 py-1 mb-2"
          />
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={shopifyOnly}
              onChange={(e) => setShopifyOnly(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600"
            />
            Shopify only
            {loadingShopifyFilter && (
              <span className="text-gray-400">(loading…)</span>
            )}
          </label>
          {loadingBrands ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <ul className="max-h-[70vh] overflow-y-auto -mx-2">
              {filteredBrands.map((b) => (
                <li key={b.id}>
                  <button
                    onClick={() => setSelectedBrandId(b.id)}
                    className={`w-full text-left px-2 py-1.5 text-sm rounded ${
                      selectedBrandId === b.id
                        ? 'bg-brand-100 text-brand-700 font-medium'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {b.displayName || b.name}
                  </button>
                </li>
              ))}
              {filteredBrands.length === 0 && (
                <li className="px-2 py-1.5 text-sm text-gray-400">No brands</li>
              )}
            </ul>
          )}
        </div>
      </aside>

      <section className="col-span-12 md:col-span-8 lg:col-span-9">
        {!selectedBrand && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
            Select a brand to configure import sources.
          </div>
        )}

        {selectedBrand && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedBrand.displayName || selectedBrand.name}
                </h2>
                <p className="text-xs text-gray-500">{selectedBrand.id}</p>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={skipCache}
                    onChange={(e) => setSkipCache(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600"
                  />
                  Skip cache
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSave}
                  disabled={!config || saving}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>

            {loadingConfig && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-sm text-gray-500">
                Loading import sources…
              </div>
            )}

            {configError && !loadingConfig && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                <p className="font-medium mb-1">Couldn&apos;t load import sources</p>
                <p className="text-xs">{configError}</p>
              </div>
            )}

            {config && !loadingConfig && (
              <>
                {config.sources.length === 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-sm text-gray-500">
                    No import sources configured yet.
                  </div>
                )}
                {config.sources.map((source) => (
                  <SourceCard
                    key={source.id}
                    source={source}
                    brandId={selectedBrand.id}
                    brandWebsite={selectedBrand.mainWebsite}
                    skipCache={skipCache}
                    onChange={(updated) => handleUpdateSource(source.id, updated)}
                    onRemove={() => handleRemoveSource(source.id)}
                    onImportNow={handleImportNow}
                    onPasteJson={(s) => setPasteFor({ source: s, reason: null })}
                  />
                ))}

                <div className="bg-white rounded-lg shadow-sm border border-dashed border-gray-300 p-4 flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2 self-center">
                    Add source:
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddSource('shopify')}
                    disabled={hasShopifySource}
                    title={hasShopifySource ? 'Only one Shopify source per brand' : ''}
                  >
                    + Shopify
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddSource('generic')}
                  >
                    + Generic URL
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddSource('json-feed')}
                  >
                    + Extractor JSON
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddSource('manual')}
                  >
                    + Manual
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddSource('square-online')}
                  >
                    + Square Online
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddSource('woocommerce')}
                  >
                    + WooCommerce
                  </Button>
                </div>
              </>
            )}

            {pasteFor && (
              <PasteStoreJsonModal
                source={pasteFor.source}
                reason={pasteFor.reason}
                onCancel={() => setPasteFor(null)}
                onDone={(entries) => {
                  const sourceId = pasteFor.source.id;
                  setPasteFor(null);
                  toast.success(`${entries.length} pasted product${entries.length === 1 ? '' : 's'} ready to review`);
                  setImportPrefetched([]);
                  setImportStoreEntries(entries);
                  setImportStoreCategories(null);
                  setImportingSourceId(sourceId);
                }}
              />
            )}

            {importingSourceId && selectedBrand && (
              <BulkImport
                key={`bulk-import-${selectedBrand.id}-${importingSourceId}`}
                brand={selectedBrand}
                autoOpen
                hideTriggerButton
                skipCache={skipCache}
                sourceKind={importingSource?.kind}
                prefetchedProducts={importPrefetched}
                prefetchedExtraction={importStoreEntries ?? undefined}
                variantGroupingRule={
                  importingSource && 'variantGroupingRule' in importingSource
                    ? importingSource.variantGroupingRule
                    : undefined
                }
                storeCategoryPicker={
                  importStoreCategories
                    ? {
                        ...importStoreCategories,
                        // Remember the unticked categories on the source, as the
                        // Shopify run remembers its excluded product types.
                        onExclusionsSaved: (excluded) => {
                          if (!selectedBrandId || !importingSourceId || !config) return;
                          const updatedSources = config.sources.map((s) =>
                            s.id === importingSourceId && (s.kind === 'woocommerce' || s.kind === 'square-online')
                              ? { ...s, excludedCategoryIds: excluded }
                              : s,
                          );
                          setConfig({ ...config, sources: updatedSources });
                          saveBrandImportSources(selectedBrandId, updatedSources).catch((err) =>
                            console.error('Failed to save excluded categories to the source:', err),
                          );
                        },
                      }
                    : undefined
                }
                autoStartUrls={autoStartUrlsForImport}
                onBrandUpdated={(updatedBrand) => {
                  // Keep the local brand list fresh so a re-open without a reload
                  // reflects the wizard's new state.
                  setBrands((prev) =>
                    prev.map((b) =>
                      b.id === updatedBrand.id ? { ...b, ...updatedBrand } : b,
                    ),
                  );
                  // Round-trip the wizard's include/exclude choices back into the
                  // brand_import_sources doc that the "Excluded product types"
                  // textarea reads from (and that handleImportNow syncs to the
                  // brand doc on the next run). The wizard only persists these to
                  // the legacy brand doc, so without this the source config keeps
                  // its pre-import excludedProductTypes and silently reverts the
                  // user's choice on the next "Import Now".
                  //
                  // This fires from the wizard's import handler (onBrandUpdated),
                  // NOT onComplete — so it lands regardless of whether the user
                  // clicks "Done — Refresh List" or just closes the modal. We
                  // also update the in-memory config so the next handleImportNow
                  // (which re-saves config.sources) can't clobber it with a stale
                  // value.
                  if (!selectedBrandId || !importingSourceId || !config) return;
                  const sourceForRun = config.sources.find(
                    (s) => s.id === importingSourceId,
                  );
                  if (!sourceForRun || sourceForRun.kind !== 'shopify') return;
                  const nextExcluded = updatedBrand.excludedProductTypes ?? [];
                  const updatedSources = config.sources.map((s) =>
                    s.id === importingSourceId && s.kind === 'shopify'
                      ? { ...s, excludedProductTypes: nextExcluded }
                      : s,
                  );
                  setConfig({ ...config, sources: updatedSources });
                  saveBrandImportSources(selectedBrandId, updatedSources).catch(
                    (err) =>
                      console.error(
                        'Failed to sync exclusions to source config:',
                        err,
                      ),
                  );
                }}
                onComplete={(stats?: BulkImportStats) => {
                  if (!selectedBrandId || !importingSourceId) return;
                  const sourceForRun = config?.sources.find(
                    (s) => s.id === importingSourceId,
                  );
                  if (!sourceForRun) return;
                  const errorCount = stats?.errorCount ?? 0;
                  const total = (stats?.addedCount ?? 0) + (stats?.updatedCount ?? 0);
                  const status =
                    errorCount > 0 && total === 0
                      ? 'error'
                      : errorCount > 0
                        ? 'partial'
                        : 'success';
                  // Exclusion round-trip already happened in onBrandUpdated; here
                  // we just record the run and refresh the config from Firestore
                  // (which preserves the exclusions appended above).
                  appendImportRun(
                    selectedBrandId,
                    importingSourceId,
                    sourceForRun.kind,
                    {
                      status,
                      importedCount: stats?.addedCount ?? 0,
                      updatedCount: stats?.updatedCount ?? 0,
                      ...(errorCount > 0
                        ? { errorMessage: `${errorCount} item${errorCount === 1 ? '' : 's'} failed` }
                        : {}),
                      // Parent/variant breakdown (Shopify variant imports)
                      ...(stats?.parentsAdded !== undefined ? { parentsImported: stats.parentsAdded } : {}),
                      ...(stats?.variantsAdded !== undefined ? { variantsImported: stats.variantsAdded } : {}),
                      ...(stats?.parentsUpdated !== undefined ? { parentsUpdated: stats.parentsUpdated } : {}),
                      ...(stats?.variantsUpdated !== undefined ? { variantsUpdated: stats.variantsUpdated } : {}),
                    },
                  )
                    .then(() => {
                      setHistoryRefreshKey((k) => k + 1);
                      return getBrandImportSources(selectedBrandId);
                    })
                    .then((next) => {
                      if (next) setConfig(next);
                    })
                    .catch((err) => {
                      console.error('Failed to record import run:', err);
                    });
                }}
                onClose={() => {
                  setImportingSourceId(null);
                  setImportPrefetched([]);
                  setImportStoreEntries(null);
                  setImportStoreCategories(null);
                }}
              />
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <span>Import history</span>
                <span className="text-xs text-gray-400">
                  {showHistory ? 'Hide' : 'Show'}
                </span>
              </button>
              {showHistory && (
                <div className="border-t border-gray-100 px-4 py-2">
                  <ImportHistoryPanel
                    brandId={selectedBrand.id}
                    sourceLabels={Object.fromEntries(
                      (config?.sources ?? []).map((s) => [
                        s.id,
                        s.label || `${s.kind}`,
                      ]),
                    )}
                    refreshKey={historyRefreshKey}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </section>
      </div>
    </div>
  );
}
